import {
  SessionSubstitutionSummary,
  formatCardTimeRange,
  formatPlainTime,
  getDiagnosticAssessmentLabel,
  getSessionSubstitutionRequestSummary,
  instantFromDate,
  instantIntervalFromDateRangeWithZone,
  instantIntervalFromRange,
  instantToDate,
  isLessonLinkAvailable,
  isMakeupDiagnosticLesson,
  isWithinN,
  type DateRange,
  type InstantInterval,
  type WeekRange,
} from '@airreading/common';
import { CoachSession, CoachSessionRemark, SessionStatus } from '@airreading/common/data';
import clsx from 'clsx';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import moment from 'moment-timezone';
import {
  MouseEventHandler,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Calendar,
  Navigate,
  momentLocalizer,
  type Components,
  type Event,
  type EventPropGetter,
  type EventProps,
  type Messages,
  type ToolbarProps,
  type View,
  type ViewsProps,
} from 'react-big-calendar';
import { Temporal } from 'temporal-polyfill';
import type { DateLocalizer } from 'react-big-calendar';

import { useTimeZone } from '@/auth/useTimeZone';
import { Button } from '@/components/ui/button';
import { CardContent } from '@/components/ui/card';
import { LoaderIcon } from '@/components/ui/loader-icon';
import { Popover, PopoverAnchor, PopoverClose, PopoverContent } from '@/components/ui/popover';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { SessionScheduleCell } from './session-schedule-cell';

import { useSession } from '@/auth/useSession';
import { CopyableLink } from '@/components/copyable-link';
import { StartOrJoinSession } from '@/components/session/start-or-join-session';
import { attendSession, recordSessionLessonLinkOpened } from '@/lib/api-calls';
import { EventDisplayTagBadge } from '@/routes/_layout/sessions/-components/event-display-tag-badge';
import { VideoConferenceTypeLabels } from '@airreading/common/data';
import { useMutation } from '@tanstack/react-query';
import './calendar.css';
import { OnDemandGLALink } from './on-demand-gla-link';
import { SessionNextClassTooltip } from './session-next-class-tooltip';

import StudentsIcon from '@/assets/icons/union-icon.svg?react';
import { AlertBanner } from '@/components/alert-banner';
import ClipIcon from '@/components/assets/clip-icon.svg?react';
import DurationIcon from '@/components/assets/duration-icon.svg?react';
import { StudentNeedsIndicators } from '@/components/student-needs-indicators';
import { useFeatureFlag } from '@airreading/feature-flags/web';

//#region Internal book-keeping

interface EventContext {
  isLoading: boolean;
  activeEventId: string | null;
  clearActiveEvent: () => void;
  sessionById: Map<string, CoachSession>;
  substitutionRequestSummaryBySessionId: Map<string, SessionSubstitutionSummary>;
  view: View;
  toolbarLeftAddon: ReactNode | null;
}

const EventContext = createContext<EventContext>({
  isLoading: false,
  activeEventId: null,
  clearActiveEvent: () => {},
  sessionById: new Map<string, CoachSession>(),
  substitutionRequestSummaryBySessionId: new Map<string, SessionSubstitutionSummary>(),
  view: 'week',
  toolbarLeftAddon: null,
});

//#endregion

//#region Public interface

export interface ExpandedEvent extends Event {
  id: string;
  title: string;
  status: SessionStatus;
  timeRangeFormatted: string;
  durationMinutes: number;
  coachSessionRemarks: CoachSessionRemark[];
}

export interface ConcreteEvent extends ExpandedEvent {
  start: Date;
  end: Date;
  startDate: Temporal.PlainDate;
  eventInterval: InstantInterval;
}

export interface SessionScheduleProps {
  isLoading?: boolean;
  events: ConcreteEvent[];
  sessionById: Map<string, CoachSession>;
  activeInstant: Temporal.Instant;
  setActiveInstant: (newInstant: Temporal.Instant) => void;
  onRangeChange?: (range: InstantInterval) => void;

  views: View[];
  view: View;
  onView: (newView: View) => void;
  toolbarLeftAddon?: ReactNode;
}

//#endregion

//#region Moment localizer (DST / arbitrary display time zone)

/**
 * `react-big-calendar`'s moment localizer iterates calendar days with
 * `moment(date).add(1, 'day')`. After `moment.tz.setDefault`, that breaks on
 * DST transition days (e.g. Africa/Cairo): the next "day" can still be the same
 * calendar date, so the week view shows duplicate columns and event layout drifts.
 *
 * This wrapper keeps moment for formatting while:
 * - building `range()` day lists via `Temporal.PlainDate` (one column per civil day)
 * - fixing `add(..., 'day')` so civil-day steps always land on the next day's midnight
 * - routing `getTimezoneOffset` / `getDstOffset` through the display `timeZone`
 *   (RBC's defaults use the browser offset, which mis-positions slots vs. events)
 * - clearing `segmentOffset`, which was compensating for the wrong offset model
 */
type LocalizerRangeUnit = Parameters<DateLocalizer['range']>[2];
type LocalizerAddUnit = Parameters<DateLocalizer['add']>[2];

function createTimeZoneAwareMomentLocalizer(timeZone: string): DateLocalizer {
  moment.tz.setDefault(timeZone);
  const localizer = momentLocalizer(moment) as DateLocalizer;
  const origRange = localizer.range.bind(localizer);
  const origAdd = localizer.add.bind(localizer);

  const calendarDayMidnightDatesBetween = (start: Date, end: Date): Date[] => {
    const zone = Temporal.TimeZone.from(timeZone);
    let day = Temporal.Instant.fromEpochMilliseconds(start.getTime()).toZonedDateTimeISO(timeZone).toPlainDate();
    const lastDay = Temporal.Instant.fromEpochMilliseconds(end.getTime()).toZonedDateTimeISO(timeZone).toPlainDate();
    const days: Date[] = [];
    while (Temporal.PlainDate.compare(day, lastDay) <= 0) {
      const zdt = day.toZonedDateTime({ timeZone: zone, plainTime: Temporal.PlainTime.from('00:00') });
      days.push(new Date(zdt.epochMilliseconds));
      day = day.add({ days: 1 });
    }
    return days;
  };

  localizer.range = (start: Date, end: Date, unit?: LocalizerRangeUnit) => {
    const u = (unit ?? 'day').toString().toLowerCase();
    if (u === 'day' || u === 'days' || u === 'd') {
      return calendarDayMidnightDatesBetween(start, end);
    }
    return origRange(start, end, unit);
  };

  localizer.add = (date: Date, amount: number, unit: LocalizerAddUnit) => {
    const u = unit.toString().toLowerCase();
    if (u === 'day' || u === 'days' || u === 'd') {
      const zonedMoment = moment.tz(date, timeZone);
      const isCalendarMidnight =
        zonedMoment.hour() === 0 &&
        zonedMoment.minute() === 0 &&
        zonedMoment.second() === 0 &&
        zonedMoment.millisecond() === 0;

      if (isCalendarMidnight) {
        return moment
          .tz(zonedMoment.format('YYYY-MM-DD'), timeZone)
          .startOf('day')
          .add(amount, 'day')
          .startOf('day')
          .toDate();
      }

      return zonedMoment.add(amount, 'day').toDate();
    }
    return origAdd(date, amount, unit);
  };

  localizer.getTimezoneOffset = (date: Date) => -moment.tz(date, timeZone).utcOffset();

  localizer.getDstOffset = (start: Date, end: Date) =>
    moment.tz(end, timeZone).utcOffset() - moment.tz(start, timeZone).utcOffset();

  const wallClockMinutes = (date: Date) => {
    const zonedDateTime = Temporal.Instant.fromEpochMilliseconds(date.getTime()).toZonedDateTimeISO(timeZone);
    return zonedDateTime.hour * 60 + zonedDateTime.minute;
  };

  localizer.getMinutesFromMidnight = (date: Date) => wallClockMinutes(date);

  localizer.getTotalMin = (start: Date, end: Date) => {
    const startZdt = Temporal.Instant.fromEpochMilliseconds(start.getTime()).toZonedDateTimeISO(timeZone);
    const endZdt = Temporal.Instant.fromEpochMilliseconds(end.getTime()).toZonedDateTimeISO(timeZone);
    const dayDelta = startZdt.toPlainDate().until(endZdt.toPlainDate(), { largestUnit: 'day' }).days;
    return dayDelta * 24 * 60 + (wallClockMinutes(end) - wallClockMinutes(start));
  };

  localizer.segmentOffset = 0;

  return localizer;
}

//#endregion

//#region Main definition

export function SessionScheduleCalendar({
  isLoading = false,
  events,
  sessionById,
  activeInstant,
  setActiveInstant,
  onRangeChange,
  views,
  view,
  onView,
  toolbarLeftAddon,
}: SessionScheduleProps) {
  const { timeZone } = useTimeZone();

  const localizer = useMemo(() => createTimeZoneAwareMomentLocalizer(timeZone), [timeZone]);

  const activeDate = useMemo(() => new Date(activeInstant.epochMilliseconds), [activeInstant]);
  const [activeEventId, setActiveEventId] = useState<string | null>(null);
  const clearActiveEvent = useCallback(() => setActiveEventId(null), [setActiveEventId]);
  const substitutionRequestSummaryBySessionId = useMemo(
    () => substitutionRequestSummariesForSessions(sessionById),
    [sessionById],
  );
  const eventContext = useMemo<EventContext>(
    () => ({
      isLoading,
      activeEventId,
      sessionById,
      clearActiveEvent,
      substitutionRequestSummaryBySessionId,
      view,
      toolbarLeftAddon: toolbarLeftAddon ?? null,
    }),
    [
      isLoading,
      activeEventId,
      sessionById,
      clearActiveEvent,
      substitutionRequestSummaryBySessionId,
      view,
      toolbarLeftAddon,
    ],
  );

  const didNavigate = useCallback(
    (newDate: Date) => {
      setActiveInstant(instantFromDate(newDate));
    },
    [setActiveInstant],
  );
  const didSelectEvent = useCallback(
    (eventLike: ExpandedEvent) => {
      const { id } = eventLike;
      setActiveEventId(id);
    },
    [setActiveEventId],
  );
  const didChangeRange = useCallback(
    (range: WeekRange | DateRange) => {
      const instantInterval =
        'end' in range
          ? instantIntervalFromDateRangeWithZone(range, timeZone)
          : instantIntervalFromRange(range, timeZone);
      onRangeChange && onRangeChange(instantInterval);
    },
    [timeZone, onRangeChange],
  );

  const didDrillDown = useCallback(
    (newDate: Date) => {
      setActiveInstant(instantFromDate(newDate));
      // Then change the view after a small delay to ensure state updates are processed
      setTimeout(() => {
        onView('day');
      }, 0);
    },
    [onView, setActiveInstant],
  );

  const eventPropGetter = useCallback<EventPropGetter<ExpandedEvent>>(
    (event) => {
      const isPast = event.end ? event.end < new Date() : false;
      const className = clsx(
        '!border-none !outline-none px-[8px]',
        view === 'day' ? 'py-[4px]' : view === 'month' ? 'py-[2px]' : 'py-[8px]',
        isPast ? 'bg-air-light-grey' : backgroundColorForCoachSessionRemarks(event.coachSessionRemarks),
      );
      return { className };
    },
    [view],
  );

  return (
    <EventContext.Provider value={eventContext}>
      <Calendar
        localizer={localizer}
        defaultView="week"
        views={views}
        view={view}
        onView={onView}
        date={activeDate}
        onNavigate={didNavigate}
        onSelectEvent={didSelectEvent}
        onRangeChange={didChangeRange}
        onDrillDown={didDrillDown}
        doShowMoreDrillDown={true}
        events={events}
        step={15}
        formats={{ eventTimeRangeFormat: emptyEventTimeRangeFormat }}
        components={customComponents}
        eventPropGetter={eventPropGetter}
        showMultiDayTimes={true}
        scrollToTime={
          new Date(
            Temporal.Instant.from(activeInstant.toString())
              .subtract({ minutes: 15 })
              .toZonedDateTimeISO(timeZone)
              .toInstant().epochMilliseconds,
          )
        }
      />
      <SessionNextClassTooltip events={events} timeZone={timeZone} />
    </EventContext.Provider>
  );
}

//#endregion

//#region UI customization

function emptyEventTimeRangeFormat() {
  return '';
}

const customComponents = {
  event: EventWrapper,
  toolbar: ToolbarWrapper,
  daySlotWrapper: DaySlotWrapper,
  timeSlotWrapper: TimeSlotWrapper as React.ComponentType,
  header: HeaderWrapper,
} as Components<ExpandedEvent> & {
  daySlotWrapper: React.ComponentType<{ children: ReactNode; value: Date }>;
  header: React.ComponentType<{ date: Date; label: string }>;
};

function ToolbarWrapper({ label, view, views, onView, onNavigate, localizer: { messages } }: ToolbarProps) {
  const { toolbarLeftAddon } = useContext(EventContext);

  return (
    <div className="flex">
      <div className="flex-1 flex items-center gap-3 text-xl mb-2">
        <span>{label}</span>
        {toolbarLeftAddon && <div className="text-base">{toolbarLeftAddon}</div>}
      </div>
      <div className="flex">
        <ToolbarNavigation onNavigate={onNavigate} />
      </div>
      <div className="flex">
        <ToolbarView view={view} onView={onView} views={views} messages={messages} />
      </div>
    </div>
  );
}

interface ToolbarNavigationProps {
  onNavigate: ToolbarProps['onNavigate'];
}

function ToolbarNavigation({ onNavigate }: ToolbarNavigationProps) {
  const { isLoading } = useContext(EventContext);
  const jumpToPreviousMonth = useCallback(() => onNavigate(Navigate.PREVIOUS), [onNavigate]);
  const jumpToNextMonth = useCallback(() => onNavigate(Navigate.NEXT), [onNavigate]);
  const jumpToToday = useCallback(() => onNavigate(Navigate.TODAY), [onNavigate]);

  return (
    <div className="flex pr-2">
      {isLoading && <LoaderIcon />}
      <Button variant="ghost" size="icon" onClick={jumpToPreviousMonth} data-testid="sessions-calendar-button-prev">
        <ChevronLeft />
      </Button>
      <Button variant="ghost" size="icon" onClick={jumpToNextMonth} data-testid="sessions-calendar-button-next">
        <ChevronRight />
      </Button>
      <Button variant="outline" onClick={jumpToToday} data-testid="sessions-calendar-button-today">
        Today
      </Button>
    </div>
  );
}

interface ToolbarViewProps {
  view: View;
  onView: (newView: View) => void;
  views: ViewsProps;
  messages: Messages;
}

function ToolbarView({ view, onView, views, messages }: ToolbarViewProps) {
  const setCurrentView = useCallback((newView: string) => onView(newView as View), [onView]);

  return (
    <Tabs value={view} onValueChange={setCurrentView} className="flex ml-auto pb-2">
      <TabsList>
        {/* This currently does not support the object form of `views`. */}
        {Array.isArray(views) &&
          views.map((view) => (
            <TabsTrigger key={view} value={view} data-testid={`sessions-calendar-button-view-${view}`}>
              {messages[view]}
            </TabsTrigger>
          ))}
      </TabsList>
    </Tabs>
  );
}

function EventWrapper({ event }: EventProps & { event: ExpandedEvent }) {
  const { sessionById, activeEventId, clearActiveEvent, substitutionRequestSummaryBySessionId } =
    useContext(EventContext);
  const { actingUser } = useSession();
  const { timeZone } = useTimeZone();
  const popoverContentRef = useRef<HTMLDivElement>(null);

  const { mutateAsync: attendSessionMutate } = useMutation({
    mutationFn: attendSession,
  });

  const didClosePopover = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      // `onSelectEvent` will trigger again unless stopped here.
      event.stopPropagation();
      clearActiveEvent();
    },
    [clearActiveEvent],
  );
  const isOpen = activeEventId === event.id;

  // Handle clicks outside the white popover box to close it
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Only close if click is outside the PopoverContent (the white box)
      if (popoverContentRef.current && !popoverContentRef.current.contains(target)) {
        clearActiveEvent();
      }
    };

    // Add listener when popover opens
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      // Remove listener when popover closes
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, clearActiveEvent]);
  const onDemandGLAFeatureEnabled = useFeatureFlag('on-demand-gla', false);
  const session = sessionById.get(event.id);

  if (!session) {
    return null;
  }
  const { startInstant, endInstant, orgName, onDemandGLAs } = session;

  const sessionHasSomeoneWithOnDemandGLA = onDemandGLAs.length > 0;

  // Generate a stable identifier based on date only (YYYY-MM-DD)
  if (!event.start) {
    return null;
  }
  const eventDate = new Date(event.start);
  const eventIdentifier = eventDate.toISOString().split('T')[0]; // YYYY-MM-DD
  const isMakeupDiagnostic =
    isMakeupDiagnosticLesson(session.lesson.name) &&
    session.students.some((s) => s.needsDiagnosticAssessment !== undefined);
  const classPlatform = session.videoConferenceType ? VideoConferenceTypeLabels[session.videoConferenceType] : null;
  const { coachIdWhoIsTeaching } = substitutionRequestSummaryBySessionId.get(session.id)!;

  const sessionUrl = session[session.videoConferenceType]?.hostUrl ?? session[session.videoConferenceType]?.attendeeUrl;

  const onStartOrJoinSessionButtonClick = async () => {
    await attendSessionMutate({ id: session.id });
  };

  return (
    <Popover open={isOpen}>
      <PopoverAnchor
        className={'w-full h-full flex flex-col items-start justify-start'}
        title="Click to view class details"
        data-testid={`sessions-calendar-event-${eventIdentifier}`}
      >
        <span className="whitespace-nowrap">{event.timeRangeFormatted}</span>
        <div
          className={clsx(
            'w-full font-semibold overflow-hidden whitespace-normal',
            event.durationMinutes >= 40 ? 'line-clamp-2' : 'line-clamp-1',
          )}
        >
          {event.title}
        </div>
      </PopoverAnchor>
      <PopoverContent
        ref={popoverContentRef}
        className="p-0 m-0 w-[400px]"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {session && (
          <CardContent
            className="p-6 flex flex-row-reverse gap-1 justify-between"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <PopoverClose
              className="h-fit [&_svg]:w-6 [&_svg]:h-6 [&_svg]:text-[#8C8C8C]"
              onClick={didClosePopover}
              data-testid={`sessions-calendar-button-close-${eventIdentifier}`}
            />
            <div className="flex flex-col gap-3">
              {/* Coach session remarks */}
              {session.coachSessionRemarksV2.length > 0 && (
                <div className="flex flex-wrap gap-2.5">
                  {session.coachSessionRemarksV2
                    .sort((a, b) => a.localeCompare(b))
                    .map((tag) => (
                      <EventDisplayTagBadge key={tag} tag={tag} session={session} />
                    ))}
                </div>
              )}
              {/* Date and time */}
              <SessionScheduleCell
                className="text-xl font-bold"
                startInstant={startInstant}
                endInstant={endInstant}
                displayTimeZone={timeZone}
                showWeekday
                data-testid={`sessions-calendar-event-time-${eventIdentifier}`}
              />
              {/* Students */}
              <div
                className="flex items-start gap-2"
                data-testid={`sessions-calendar-event-students-${eventIdentifier}`}
              >
                <StudentsIcon />
                <div>
                  {[...session.students]
                    .sort((a, b) => (a.coachFacingDisplayName ?? '').localeCompare(b.coachFacingDisplayName ?? ''))
                    .map((s) => {
                      const studentDisplayName = s.coachFacingDisplayName ?? '';
                      const diagnosticLabel = isMakeupDiagnostic
                        ? getDiagnosticAssessmentLabel(s.needsDiagnosticAssessment)
                        : null;
                      const onDemandGLA = session.onDemandGLAs.find(({ studentId }) => studentId === s.id);
                      return (
                        <div key={s.id} className="flex gap-x-1 items-center justify-start">
                          {studentDisplayName} {diagnosticLabel}{' '}
                          {onDemandGLA && (
                            <OnDemandGLALink
                              onDemandGLA={onDemandGLA}
                              grade={s.grade}
                              dataTestId={`sessions-calendar-event-on-demand-gla-lesson-${eventIdentifier}`}
                            />
                          )}
                          <StudentNeedsIndicators
                            specialEd={s.specialEd}
                            dyslexic={s.dyslexic}
                            ell={s.ell}
                            mode="icons"
                          />
                        </div>
                      );
                    })}
                </div>
              </div>
              {/* Duration */}
              <div
                className="flex items-center gap-2"
                data-testid={`sessions-calendar-event-duration-${eventIdentifier}`}
              >
                <DurationIcon />
                <Duration start={instantToDate(startInstant)} end={instantToDate(endInstant)} />
              </div>
              {/* Lessons */}
              <div className="flex items-center gap-2">
                <ClipIcon />
                {isLessonLinkAvailable(session.lessonLink, startInstant) ? (
                  <CopyableLink
                    className="text-air-primary"
                    externalHref={session.lessonLink!}
                    canCopyUrl={false}
                    showExternalHrefIcon={false}
                    onExternalClick={() => {
                      void recordSessionLessonLinkOpened(session.id).catch(() => {});
                    }}
                    data-testid={`sessions-calendar-event-lesson-${eventIdentifier}`}
                  >
                    {session.lesson.name}
                  </CopyableLink>
                ) : (
                  <span data-testid={`sessions-calendar-event-lesson-${eventIdentifier}`}>{session.lesson.name}</span>
                )}
              </div>
              {/** GLA alert */}
              {onDemandGLAFeatureEnabled && sessionHasSomeoneWithOnDemandGLA && (
                <AlertBanner
                  title="Please have both assessment and lesson ready before class, and assess student first."
                  content={null}
                />
              )}
              {/* Start button */}
              {coachIdWhoIsTeaching === actingUser.id && session.status !== 'cancelled' && (
                <StartOrJoinSession
                  label={`Start ${classPlatform ?? 'Class'}`}
                  url={sessionUrl}
                  startButtonProps={{
                    className: 'bg-air-primary hover:bg-air-primary/80',
                    icon: undefined,
                    onClick: onStartOrJoinSessionButtonClick,
                    'data-testid': `sessions-calendar-button-start-${eventIdentifier}`,
                  }}
                  whenUrlEmptyRender={`No ${classPlatform ?? 'meeting'} url`}
                />
              )}
              {/* District name */}
              {orgName && <div data-testid={`sessions-calendar-event-district-${eventIdentifier}`}>{orgName}</div>}
              {/* Assessments - WIP */}
              {/* session.lesson.assessments && (
                <div className="grid auto-rows-min grid-cols-2-auto-fix">
                  {session.lesson.assessments.map((assessment) => (
                    <div key={assessment.id}>{assessment.name}</div>
                  ))}
                </div>
              ) */}
              {/* Substitution-related info are all out of scope for now in calendar view */}
              {/* Substitution alert */}
              {/* {involvementMessage && <SessionSubstituteAlert type="confirm">{involvementMessage}</SessionSubstituteAlert>} */}
              {/* <div className="mt-2">
                {coachIdWhoCanRequestSub === actingUser.id && (
                  <RequestSessionSubstitute
                    trigger={
                      <Button variant={'outline'} size={'sm'} className="h-5 rounded-md px-4">
                        Request substitution
                      </Button>
                    }
                    startInstant={session.startInstant}
                    endInstant={session.endInstant}
                    lessonName={session.lesson.name}
                    sessionId={session.id}
                  />
                )}
              </div> */}
            </div>
          </CardContent>
        )}
      </PopoverContent>
    </Popover>
  );
}

function Duration(range: DateRange) {
  const [start, end] = [instantFromDate(range.start), instantFromDate(range.end)];
  const { minutes } = start.until(end).round({ largestUnit: 'minute' });
  return <>{minutes} minutes</>;
}

function DaySlotWrapper({ children, value }: { children: ReactNode; value: Date }) {
  const dateStr = value.toISOString().split('T')[0]; // YYYY-MM-DD format
  return (
    <div data-testid={`sessions-calendar-day-slot-${dateStr}`} className="rbc-day-slot">
      {children}
    </div>
  );
}

function TimeSlotWrapper({ children, value }: { children: ReactNode; value: Date }) {
  const hours = value.getHours();
  const minutes = value.getMinutes();
  const timeStr = `${hours.toString().padStart(2, '0')}-${minutes.toString().padStart(2, '0')}`;
  return (
    <div data-testid={`sessions-calendar-time-slot-${timeStr}`} className="rbc-time-slot">
      {children}
    </div>
  );
}

function HeaderWrapper({ date, label }: { date: Date; label: string }) {
  const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD format
  return (
    <div data-testid={`sessions-calendar-header-${dateStr}`} className="rbc-header">
      {label}
    </div>
  );
}

//#endregion

//#region Exported utility functions

/**
 * The result of generating calendar events from sessions.
 */
export interface EventsWithContext {
  /**
   * An array of events usable by the calendar.
   */
  events: ConcreteEvent[];

  /**
   * A lookup table from each event's ID to the session it was generated from.
   */
  sessionById: Map<string, CoachSession>;
}

/**
 * This helper function is intentionally exported for <SessionScheduleCalendar>
 * consumers to use. It is not internally memoized by the component because this
 * helper calculates the earliest event's start date, which is often useful as
 * the starting date for a calendar. A default, starting date can only be
 * specified by the parent component that controls the calendar.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function eventsFromSessions(sessions: CoachSession[], timeZone: string): EventsWithContext {
  const events: ConcreteEvent[] = [];
  const sessionById = new Map<string, CoachSession>();
  if (!sessions.length) {
    return { events, sessionById };
  }

  for (const session of sessions) {
    const title = studentNamesOfSession(session).join(', ');

    const { id, startInstant, endInstant, status, hoursCanceledBeforeStart, coachSessionRemarks } = session;
    sessionById.set(id, session);

    if (
      status === SessionStatus.cancelled &&
      (hoursCanceledBeforeStart === null || !isWithinN(hoursCanceledBeforeStart, 12))
    ) {
      continue;
    }

    const start = instantToDate(startInstant);
    const end = instantToDate(endInstant);

    if (start > end) {
      continue;
    }

    const startInstantZone = startInstant.toZonedDateTimeISO(timeZone);
    const startTime = formatPlainTime(startInstantZone.toPlainTime());
    const endTime = formatPlainTime(endInstant.toZonedDateTimeISO(timeZone).toPlainTime());
    const timeRangeFormatted = formatCardTimeRange(startTime, endTime);
    const startDate = startInstantZone.toPlainDate();
    const eventInterval = instantIntervalFromRange({ start, end }, timeZone);

    const durationMinutes = startInstant.until(endInstant).round({ largestUnit: 'minute' }).minutes;

    events.push({
      id,
      title,
      start,
      end,
      timeRangeFormatted,
      durationMinutes,
      status,
      startDate,
      eventInterval,
      allDay: false,
      coachSessionRemarks,
    });
  }

  return { events, sessionById };
}

//#endregion

//#region Internal utility functions

function studentNamesOfSession(session: CoachSession): string[] {
  return session.students.map((s) => s.coachFacingDisplayName ?? '').toSorted();
}

function substitutionRequestSummariesForSessions(
  sessionsById: EventContext['sessionById'],
): EventContext['substitutionRequestSummaryBySessionId'] {
  const result = new Map<string, SessionSubstitutionSummary>();
  for (const [id, session] of sessionsById.entries()) {
    result.set(id, getSessionSubstitutionRequestSummary(session));
  }
  return result;
}

function backgroundColorForCoachSessionRemarks(coachSessionRemarks: CoachSessionRemark[]) {
  const isCancelledWithIn12Hours = coachSessionRemarks.includes(CoachSessionRemark.canceledWithin12Hours);
  const isSubbedWithin12Hours = coachSessionRemarks.includes(CoachSessionRemark.subClassWithin12Hours);
  const isSubbedOutside12Hours = coachSessionRemarks.includes(CoachSessionRemark.subClass);

  return {
    'bg-event-scheduled': !isCancelledWithIn12Hours && !isSubbedWithin12Hours && !isSubbedOutside12Hours,
    'bg-event-cancelled': isCancelledWithIn12Hours,
    'bg-event-subbed-outside-12-hours': !isCancelledWithIn12Hours && isSubbedOutside12Hours,
    'bg-event-subbed-within-12-hours': !isCancelledWithIn12Hours && isSubbedWithin12Hours,
  };
}

//#endregion
